import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { api } from "../api/client";
import { IconLoader } from "./Icons";
import { useMsg } from "../hooks/useMsg";

interface NumberingBranch {
  id: string;
  code: string;
  name: string;
  format: string | null;
  sample: string;
}

interface NumberingConfig {
  mode: "shared" | "per_branch";
  reset: "none" | "yearly" | "monthly" | "daily";
  shared_format: string;
  branches: NumberingBranch[];
}

const COUNTER_TOKENS = ["{CC}", "{CCC}", "{CCCC}", "{CCCCC}", "{CCCCCC}"];
const DATE_TOKENS = ["{YYYY}", "{YY}", "{MM}", "{DD}"];
const ALL_TOKENS = new Set([...COUNTER_TOKENS, ...DATE_TOKENS]);
const PLACEHOLDER_RE = /\{[^{}]*\}/g;
const ALLOWED_TEXT_RE = /^[A-Za-z0-9]+$/;
const MAX_RENDERED_LEN = 20;

function validateFormat(fmt: string, t: TFunction): string | null {
  if (!fmt) return t("numbering.errEmpty");

  const placeholders: string[] = [];
  const literalParts: string[] = [];
  let lastEnd = 0;
  let m: RegExpExecArray | null;
  PLACEHOLDER_RE.lastIndex = 0;
  while ((m = PLACEHOLDER_RE.exec(fmt)) !== null) {
    if (m.index > lastEnd) literalParts.push(fmt.slice(lastEnd, m.index));
    const token = m[0];
    if (!ALL_TOKENS.has(token)) return t("numbering.errInvalidToken", { token });
    placeholders.push(token);
    lastEnd = m.index + token.length;
  }
  if (lastEnd < fmt.length) literalParts.push(fmt.slice(lastEnd));

  for (const part of literalParts) {
    if (part && !ALLOWED_TEXT_RE.test(part)) {
      return t("numbering.errAlphanumeric");
    }
  }

  const counterTokens = placeholders.filter((p) => COUNTER_TOKENS.includes(p));
  if (counterTokens.length !== 1) {
    return t("numbering.errOneCounter");
  }

  const seen = new Set<string>();
  for (const p of placeholders) {
    if (seen.has(p)) return t("numbering.errDuplicateToken", { token: p });
    seen.add(p);
  }

  if (fmt.split("{").length !== fmt.split("}").length) {
    return t("numbering.errUnclosedBrace");
  }

  // Max rendered length check.
  let renderedLen = 0;
  lastEnd = 0;
  PLACEHOLDER_RE.lastIndex = 0;
  while ((m = PLACEHOLDER_RE.exec(fmt)) !== null) {
    if (m.index > lastEnd) renderedLen += m.index - lastEnd;
    const token = m[0];
    if (token === "{YYYY}") renderedLen += 4;
    else if (token === "{YY}") renderedLen += 2;
    else if (token === "{MM}" || token === "{DD}") renderedLen += 2;
    else {
      // counter
      renderedLen += token.length - 2;
    }
    lastEnd = m.index + token.length;
  }
  if (lastEnd < fmt.length) renderedLen += fmt.length - lastEnd;

  if (renderedLen > MAX_RENDERED_LEN) {
    return t("numbering.errTooLong", { max: MAX_RENDERED_LEN, rendered: renderedLen });
  }

  return null;
}

function previewNumber(fmt: string, seq = 1): string {
  const now = new Date();
  return fmt
    .replace("{YYYY}", String(now.getFullYear()).padStart(4, "0"))
    .replace("{YY}", String(now.getFullYear() % 100).padStart(2, "0"))
    .replace("{MM}", String(now.getMonth() + 1).padStart(2, "0"))
    .replace("{DD}", String(now.getDate()).padStart(2, "0"))
    .replace("{CC}", String(seq).padStart(2, "0"))
    .replace("{CCC}", String(seq).padStart(3, "0"))
    .replace("{CCCC}", String(seq).padStart(4, "0"))
    .replace("{CCCCC}", String(seq).padStart(5, "0"))
    .replace("{CCCCCC}", String(seq).padStart(6, "0"));
}

export default function NumberingSettings() {
  const { t } = useTranslation();
  const [cfg, setCfg] = useState<NumberingConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const setMsg = useMsg();

  const [mode, setMode] = useState<"shared" | "per_branch">("shared");
  const [reset, setReset] = useState<"none" | "yearly" | "monthly" | "daily">("none");
  const [sharedFormat, setSharedFormat] = useState("");
  const [branchFormats, setBranchFormats] = useState<Record<string, string>>({});

  useEffect(() => {
    setLoading(true);
    api
      .get<NumberingConfig>("/tenant/document-numbering")
      .then(({ data }) => {
        setCfg(data);
        setMode(data.mode);
        setReset(data.reset);
        setSharedFormat(data.shared_format);
        const map: Record<string, string> = {};
        for (const b of data.branches) {
          map[b.id] = b.format ?? "";
        }
        setBranchFormats(map);
      })
      .catch(() => setMsg({ type: "err", text: t("numbering.loadError") }))
      .finally(() => setLoading(false));
  }, []);

  const sharedError = useMemo(() => validateFormat(sharedFormat, t), [sharedFormat, t]);

  const branchErrors = useMemo(() => {
    if (mode !== "per_branch") return null;
    const out: Record<string, string | null> = {};
    for (const b of cfg?.branches || []) {
      const fmt = branchFormats[b.id] || "";
      out[b.id] = fmt ? validateFormat(fmt, t) : null;
    }
    return out;
  }, [branchFormats, cfg, mode, t]);

  const hasErrors = sharedError || Object.values(branchErrors || {}).some(Boolean);

  async function save() {
    if (hasErrors) {
      setMsg({ type: "err", text: t("numbering.fixErrors") });
      return;
    }
    setSaving(true);
    setMsg(null);

    // Send branch formats only for non-empty (overridden) ones.
    const overrides: Record<string, string | null> = {};
    if (mode === "per_branch") {
      for (const b of cfg?.branches || []) {
        const fmt = branchFormats[b.id]?.trim() || null;
        overrides[b.id] = fmt;
      }
    }

    try {
      const { data } = await api.put<NumberingConfig>("/tenant/document-numbering", {
        mode,
        reset,
        shared_format: sharedFormat,
        branch_formats: mode === "per_branch" ? overrides : {},
      });
      setCfg(data);
      setMode(data.mode);
      setReset(data.reset);
      setSharedFormat(data.shared_format);
      const map: Record<string, string> = {};
      for (const b of data.branches) map[b.id] = b.format ?? "";
      setBranchFormats(map);
      setMsg({ type: "ok", text: t("numbering.saved") });
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setMsg({ type: "err", text: typeof detail === "string" ? detail : t("numbering.saveError") });
    } finally {
      setSaving(false);
    }
  }

  if (loading || !cfg) {
    return (
      <div className="card" style={{ textAlign: "center", padding: 24 }}>
        <IconLoader size={24} className="muted" />
      </div>
    );
  }

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>{t("numbering.title")}</h3>

      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          <input
            type="radio"
            name="numbering_mode"
            checked={mode === "shared"}
            onChange={() => setMode("shared")}
          />
          {t("numbering.modeShared")}
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          <input
            type="radio"
            name="numbering_mode"
            checked={mode === "per_branch"}
            onChange={() => setMode("per_branch")}
          />
          {t("numbering.modePerBranch")}
        </label>
      </div>

      <div style={{ marginBottom: 20 }}>
        <label>{t("numbering.resetLabel")}</label>
        <select
          value={reset}
          onChange={(e) => setReset(e.target.value as "none" | "yearly" | "monthly" | "daily")}
          style={{ width: "100%", maxWidth: 320 }}
        >
          <option value="none">{t("numbering.resetNone")}</option>
          <option value="yearly">{t("numbering.resetYearly")}</option>
          <option value="monthly">{t("numbering.resetMonthly")}</option>
          <option value="daily">{t("numbering.resetDaily")}</option>
        </select>
        <p style={{ fontSize: 12, color: "var(--muted)", marginTop: 6 }}>
          {t("numbering.resetHint")}
        </p>
      </div>

      <div style={{ marginBottom: 20 }}>
        <label>{t("numbering.sharedFormat")}</label>
        <input
          value={sharedFormat}
          onChange={(e) => setSharedFormat(e.target.value)}
          style={{ width: "100%", maxWidth: 400, fontFamily: "monospace" }}
          placeholder="{YYYY}{MM}{DD}{CCCC}"
        />
        <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 6 }}>
          {t("numbering.supportedTokens")}
          <ul style={{ margin: "6px 0 0", paddingLeft: 18, listStyle: "none" }}>
            <li><code>{"{YYYY}"}</code> — {t("numbering.tokYear4")}</li>
            <li><code>{"{YY}"}</code> — {t("numbering.tokYear2")}</li>
            <li><code>{"{MM}"}</code> — {t("numbering.tokMonth")}</li>
            <li><code>{"{DD}"}</code> — {t("numbering.tokDay")}</li>
            <li><code>{"{CC}"}</code> — {t("numbering.tokSeq", { n: 2 })}</li>
            <li><code>{"{CCC}"}</code> — {t("numbering.tokSeq", { n: 3 })}</li>
            <li><code>{"{CCCC}"}</code> — {t("numbering.tokSeq", { n: 4 })}</li>
            <li><code>{"{CCCCC}"}</code> — {t("numbering.tokSeq", { n: 5 })}</li>
            <li><code>{"{CCCCCC}"}</code> — {t("numbering.tokSeq", { n: 6 })}</li>
          </ul>
        </div>
        {sharedError ? (
          <div style={{ fontSize: 13, color: "var(--red)", marginTop: 4 }}>{sharedError}</div>
        ) : (
          <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>
            {t("common.preview")}: <strong>{previewNumber(sharedFormat)}</strong>
          </div>
        )}
      </div>

      {mode === "per_branch" && (
        <table style={{ marginBottom: 20, width: "100%" }}>
          <thead>
            <tr>
              <th>{t("common.branch")}</th>
              <th>{t("numbering.colFormat")}</th>
              <th>{t("common.preview")}</th>
            </tr>
          </thead>
          <tbody>
            {cfg.branches.map((b) => {
              const fmt = branchFormats[b.id] || "";
              const err = branchErrors?.[b.id];
              return (
                <tr key={b.id}>
                  <td>
                    <strong>{b.name}</strong>
                    <div style={{ fontSize: 12, color: "var(--muted)" }}>{b.code}</div>
                  </td>
                  <td>
                    <input
                      value={fmt}
                      onChange={(e) =>
                        setBranchFormats((prev) => ({ ...prev, [b.id]: e.target.value }))
                      }
                      placeholder={sharedFormat}
                      style={{ fontFamily: "monospace", width: "100%", maxWidth: 300 }}
                    />
                    {err && <div style={{ fontSize: 12, color: "var(--red)" }}>{err}</div>}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {err ? "—" : <strong>{previewNumber(fmt || sharedFormat)}</strong>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <button className="primary" onClick={save} disabled={saving || !!hasErrors}>
          {saving ? t("common.saving") : t("numbering.save")}
        </button>
      </div>
    </div>
  );
}
